// src/components/Header.js
import React, { useState, useRef, useEffect } from 'react';
import { FaPhoneAlt, FaEnvelope, FaBars, FaTimes } from 'react-icons/fa';
import FocusTrap from 'focus-trap-react';
import { enableBodyScroll } from 'body-scroll-lock';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import logo from '../assets/logo.svg';
import '../styles/Header.css';

const servicesLinks = [
  { to: '/services/tree-pruning', label: 'Tree Pruning' },
  { to: '/services/tree-removal', label: 'Tree Removal' },
  { to: '/services/stump-grinding', label: 'Stump Grinding' },
  { to: '/services/emergency-services', label: 'Emergency Services' },
  { to: '/services/tree-health', label: 'Tree Health' },
  { to: '/services/land-clearing', label: 'Land Clearing' }
];

const Header = () => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [isDesktopServicesDropdownOpen, setDesktopServicesDropdownOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (to) => {
    console.log('Navigation clicked:', to);
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    enableBodyScroll(mobileMenuRef.current);
    navigate(to);
  };

  const handleMobileMenuKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setMobileMenuOpen(prev => !prev);
    }
  };

  const navItems = [
    { to: '/', label: 'Home' },
    { type: 'services', label: 'Services' },
    { to: '/crew', label: 'Crew' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/about-us', label: 'About Us' },
    { to: '/contact', label: 'Contact' }
  ];

  return (
    <>
      <header className={`header-container ${isScrolled ? 'scrolled' : ''}`}>
        <Link to="/">
          <img src={logo} alt="Grizzly Tree Company Logo" className="logo" />
        </Link>

        <nav className="nav-menu" aria-label="Primary Navigation">
          {navItems.map((item) => {
            if (item.type === 'services') {
              return (
                <div 
                  key="services" 
                  className="nav-item services-dropdown"
                  onMouseEnter={() => setDesktopServicesDropdownOpen(true)}
                  onMouseLeave={() => setDesktopServicesDropdownOpen(false)}
                >
                  <span>Services</span>
                  {isDesktopServicesDropdownOpen && (
                    <div className="dropdown-menu-container">
                      <div className="dropdown-menu">
                        {servicesLinks.map((service) => (
                          <Link
                            key={service.to}
                            className={`dropdown-item ${location.pathname === service.to ? 'active' : ''}`}
                            to={service.to}
                          >
                            {service.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.to}
                className={`nav-item ${location.pathname === item.to ? 'active' : ''}`}
                to={item.to}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="contact-icons">
          <a href="tel:+17753095630" aria-label="Call Grizzly Tree Co.">
            <FaPhoneAlt title="Phone" />
          </a>
          <a href="mailto:grizzlytreeco.99@gmail.com" aria-label="Email Grizzly Tree Co.">
            <FaEnvelope title="Email" />
          </a>
        </div>

        <div
          className="mobile-menu-icon"
          onClick={() => setMobileMenuOpen(prev => !prev)}
          onKeyDown={handleMobileMenuKeyDown}
          aria-label="Open Mobile Menu"
          role="button"
          tabIndex={0}
        >
          <FaBars />
        </div>
      </header>

      <FocusTrap active={isMobileMenuOpen}>
        <nav
          className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
          ref={mobileMenuRef}
        >
          <div className="close-icon" onClick={() => setMobileMenuOpen(false)}>
            <FaTimes />
          </div>
          
          <Link to="/" onClick={() => setMobileMenuOpen(false)}>
            <img src={logo} alt="Grizzly Tree Company Logo" className="mobile-menu-logo" />
          </Link>

          {navItems.map((item, index) => {
            if (item.type === 'services') {
              return (
                <div
                  key="services"
                  className={`mobile-nav-item services-dropdown ${isServicesDropdownOpen ? 'active' : ''}`}
                  onClick={() => setServicesDropdownOpen(prev => !prev)}
                >
                  Services
                  {isServicesDropdownOpen && (
                    <div className="mobile-services-submenu">
                      {servicesLinks.map((service) => (
                        <div
                          key={service.to}
                          className={`mobile-nav-item ${location.pathname === service.to ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavClick(service.to);
                          }}
                        >
                          {service.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div
                key={item.to}
                className={`mobile-nav-item ${isMobileMenuOpen ? 'open' : ''}`}
                onClick={() => handleNavClick(item.to)}
                style={{
                  transitionDelay: `${index * 0.1}s`
                }}
              >
                {item.label}
              </div>
            );
          })}

          <div
            className="contact-icons"
            style={{
              display: 'flex',
              marginTop: 'auto',
              justifyContent: 'center',
              opacity: isMobileMenuOpen ? 1 : 0,
              transition: 'opacity 0.3s ease',
              transitionDelay: '0.3s'
            }}
          >
            <a href="tel:+17753095630">
              <FaPhoneAlt />
            </a>
            <a href="mailto:grizzlytreeco.99@gmail.com">
              <FaEnvelope />
            </a>
          </div>
        </nav>
      </FocusTrap>

      <div
        className={`backdrop ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
    </>
  );
};

export default Header;